import { useState, useCallback } from "react";
import axios from "axios";
import { Moment } from "moment";
import cSharpApi from "../../../../../../services/api/CSharpApi";

export type WaterVolumeTemperatureHistoricalData = {
  volumeData: WaterVolumeDatum;
  temperatureData: WaterTemperatureDatum;
  hasTemperatureData: boolean;
};

export type WaterVolumeDatum = {
  time: Moment;
  volume: number;
  maxVolume: number;
  volumeDisplayUnit: string;
  isOver: boolean;
  percentageOver?: number;
  label: string;
};

export type WaterTemperatureDatum = {
  time: Moment;
  temperature: number;
  temperatureDisplayUnit: string;
  label: string;
};

export type WaterVolumeTemperatureChartData = {
  historicalData: WaterVolumeTemperatureHistoricalData[];
  hasData: boolean;
  errorMessage: string;
};

type TankVolumeTemperatureChartState = {
  data: WaterVolumeTemperatureChartData;
  isLoading: boolean;
  hasError: boolean;
  errorMessage?: string;
  message?: string;
};

type FetchParams = {
  duration: number;
  resolution: number;
  customEndDate?: number;
  hoursToAdd: number;

  volumeName: string;
  volumeFieldKey: string;
  temperatureName: string;
  temperatureFieldKey: string;
  tankArea: number;
  tankDepth: number;

  volumeDisplayName?: string;
  volumeFieldName?: string;
  volumePortNumber: number;
  temperatureDisplayName?: string;
  temperatureFieldName?: string;
  temperaturePortNumber: number;
  orgId?: string;
};

const INITIAL_STATE = {
  data: {
    historicalData: [],
    hasData: false,
    errorMessage: "",
  },
  isLoading: false,
  hasError: false,
  errorMessage: "",
  message: "",
};

const useHistoricalWaterVolTemp = (deviceId: string) => {
  const [fetchedData, setFetchedData] = useState<TankVolumeTemperatureChartState>(INITIAL_STATE);

  const cancelTokenSource = axios.CancelToken.source();

  const fetchData = useCallback(
    async (params: FetchParams) => {
      setFetchedData({
        ...INITIAL_STATE,
        isLoading: true,
      });

      try {
        const response = await cSharpApi.post(
          `charts/get-historical-water-volume-and-temperature/${deviceId}`,
          JSON.stringify(params),
          {
            cancelToken: cancelTokenSource.token,
          },
        );

        const data = await response.data.data;

        if (data) {
          setFetchedData({
            data,
            isLoading: false,
            hasError: false,
            errorMessage: "",
            message: response.data.message,
          });
        }
      } catch (error) {
        let errorMessage = "fetching data aborted";

        if (axios.isCancel(error)) {
          console.log("fetching data aborted");
        } else {
          errorMessage = error instanceof Error ? error.message : "Unknown error";
          console.log("An error occurred", error);
        }

        setFetchedData({
          ...INITIAL_STATE,
          errorMessage,
        });
      }
      return () => cancelTokenSource.cancel();
    },
    [deviceId],
  );

  const { data, isLoading, hasError, errorMessage, message } = fetchedData;

  return { fetchData, data, isLoading, hasError, errorMessage, message };
};

export default useHistoricalWaterVolTemp;
